import React, { PropsWithChildren } from 'react';
import { useNavigate } from "react-router";
import { Fab as FloatingActionButton, FabProps } from '@mui/material';
import {
  School as EducationIcon,
} from '@mui/icons-material';
import { styled } from '@mui/styles';
import { EducationalVideosStringAssets } from '../../../../assets/stringAssets/EducationalVideosStringAssets';


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled FloatingActionButton object to represent the button
const StyledFloatingActionButton = styled((props: FabProps) => (
  <FloatingActionButton
    {...props}
  />
))(({ theme }) => ({
  // MuiFab theming isn't responding properly, so directly theme the hover properties
  '&:hover': {
    background: theme.floatingActionButton.hover.background,
    border: theme.floatingActionButton.hover.border,
    color: theme.floatingActionButton.hover.color,
  },

  [theme.breakpoints.up('xs')]: {
    transform: 'scale(.80) translate(-20%)',
  },
  [theme.breakpoints.up('sm')]: {
    transform: 'scale(.85) translate(-15%)',
  },
  [theme.breakpoints.up('md')]: {
    transform: 'scale(.90) translate(-10%)',
  },
  [theme.breakpoints.up('lg')]: {
    transform: 'scale(.95) translate(-5%)',
  },
  [theme.breakpoints.up('xl')]: {
    transform: 'scale(1.0)',
  },
}));

/**
 * @interface IEducationalVideosButtonProps declares any input properties required for this component.
 */
export interface IEducationalVideosButtonProps extends PropsWithChildren<unknown> {
  // Id of the video catalog to which the button will navigate
  videoCatalogId: string;
}

/**
 * @function EducationalVideosButton is a React functional component representing a button that will appear on a page and its only 
 *           action is to navigate to a given Url if pressed.
 *           (NOTE: Appearance styling for the Fab (FloatingActionButton), but not sizing, is addressed in the global theme.)
 * @param {} props are the properties passed into the component.
 */
export const EducationalVideosButton: React.FC<IEducationalVideosButtonProps> = (props: IEducationalVideosButtonProps) => {
  EducationalVideosButton.displayName = 'Educational Videos Button';

  const { videoCatalogId } = props;

  const navigate = useNavigate();

  // create a navigation Url using the baseDomain and the videoCatalogId
  const navigationUrl: string = `/${EducationalVideosStringAssets.educationalVideosBaseRoute}/${videoCatalogId}`

  return (
    <>
      <StyledFloatingActionButton onClick={() => navigate(navigationUrl)}>
        <EducationIcon />
      </StyledFloatingActionButton>
    </>
  )
}
